/**
 * Checkout PPS Composable
 * Phase 2.5: PPS Extras Data
 *
 * Thin wrapper over checkout store PPS state.
 * Produces UI-ready extras groups. Inbound/outbound transport legs (TRANSHIN / TRANSRÜCK)
 * are merged into a single paired group so one selection writes both BookingCodeIds to
 * the store; all other extras render as today's single groups.
 */
import { computed } from 'vue';
import { useCheckoutStore } from '@/stores/checkout';
import { $t } from '@/composables/translations/useTranslate';
import type { PpsService } from '@/types/schemas/pps';
import { buildExtrasGroups } from './transportPairing';
import type {
  ExtraGroupDisplayItem,
  ExtraGroupPair,
  ExtraGroupSingle,
  ServiceTypeDisplayItem,
} from './useCheckoutPps.types';

export type { ExtraGroupDisplayItem, ExtraGroupPair, ExtraGroupSingle, ServiceTypeDisplayItem };

export function useCheckoutPps() {
  const checkout = useCheckoutStore();

  const isLoading = computed(() => checkout.isLoadingPps);
  const error = computed(() => checkout.ppsError);
  const hasData = computed(() => checkout.hasPpsData);
  const rawExtras = computed(() => checkout.ppsExtras);

  const selectedExtrasPrice = computed(() => checkout.selectedExtrasPrice);

  async function fetchData(): Promise<void> {
    await checkout.fetchPpsExtras();
  }

  function selectServiceType(bookingCodeId: number, serviceTypeCode: string): void {
    checkout.selectServiceType(bookingCodeId, serviceTypeCode);
  }

  function getSelectedServiceTypeCode(bookingCodeId: number): string | undefined {
    return checkout.selectedExtrasMap.get(bookingCodeId);
  }

  function selectPairedServiceType(
    inboundId: number,
    outboundId: number,
    serviceTypeCode: string
  ): void {
    checkout.selectPairedServiceType(inboundId, outboundId, serviceTypeCode);
  }

  function getSelectedPairedServiceTypeCode(
    inboundId: number,
    outboundId: number
  ): string | undefined {
    return checkout.getSelectedPairedServiceTypeCode(inboundId, outboundId);
  }

  const extrasGroups = computed((): ExtraGroupDisplayItem[] => {
    const pairTitle = $t('ibe.checkout.pps.transport-pair', 'Transfer (outbound + return)');
    // Cast past Vue's DeepReadonly — buildExtrasGroups only reads.
    return buildExtrasGroups(rawExtras.value as readonly PpsService[], pairTitle);
  });

  return {
    isLoading,
    error,
    hasData,
    rawExtras,
    selectedExtrasPrice,
    extrasGroups,
    fetchData,
    selectServiceType,
    getSelectedServiceTypeCode,
    selectPairedServiceType,
    getSelectedPairedServiceTypeCode,
  };
}
